import React from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { HtmlTooltipDisabled, StyledInfoBox } from "../../../components/commonComponents/StyledComponents/styled";
import TextInputField from "../../../components/commonComponents/TextInputs/TextInputField";

import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../Store/actions";

import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import { Close, Save } from "@mui/icons-material";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import {
  checkUserDetailsFromCookie,
  checkUserPermissions,
  checkWorkSpaceFromCookies,
  fetchRoles,
  saveUserInformation,
} from "../../../api_calls/userManagement";
import {
  getBottomNavRetailerSelectionFromCookies,
  isLockedOutsideAllRetailers,
  resolveRetailerSelectionOptions,
} from "../../../api_calls/retailerSelection";
import { fetchCompanies, fetchCompanyHierarchy } from "../../../api_calls/companyRequests";
import { toast } from "react-toastify";
import { fetchCampaignNames } from "../../../api_calls/campaignRequests";
import SingleSelect from "../../../components/commonComponents/Selectors/SingleSelect";
import MultiSelect from "../../../components/commonComponents/Selectors/MultiSelect";
import GroupedMultiSelect from "../../../components/commonComponents/Selectors/GroupedMultiSelect";
import CompanyHierarchySingleSelect from "../../../components/commonComponents/Selectors/CompanyHierarchySingleSelect";
import {
  ADD_TO_NEW_COMPANY,
  ALREADY_ADDED_COMPANY_TOOLTIP,
  isAlreadyAddedCompany,
  markAlreadyAddedCompanies,
  nationalCompaniesOnly,
  toCompanyOption,
} from "./companyPickerUtils";

const AddUser = ({ open, onClose, size, usedFor, userInfo, closeModal, cyreenBoolean, refreshUserType }) => {
  const [selectedRolePermissions, setSelectedRolePermissions] = React.useState(
    usedFor === "update" && Array.isArray(userInfo.roles)
      ? userInfo.roles.map((roleName) => {
          return { value: roleName, label: roleName };
        })
      : []
  );
  const [userDetails, setUserDetails] = React.useState(userInfo);
  const adduserText = cyreenBoolean === true ? "Add New Cyreen User" : "Add New User";
  const updateuserText = cyreenBoolean === true ? "Update Cyreen User" : "Update User";
  // Roles are for the user being added, not the company level in the hierarchy.
  const roleListType = cyreenBoolean === true ? "cyreen" : "retailer";

  // Logged-in user (from cookies)
  const checkUserDetail = checkUserDetailsFromCookie();
  const userData = checkUserDetail.success === true ? checkUserDetail.user_detail : {};
  const loggedInUserType = typeof userData.user_type !== "undefined" ? userData.user_type.toLowerCase() : "";

  // Current workspace cookie — used as the default company when the dropdown is hidden
  const checkCurrentWorkSpaces = checkWorkSpaceFromCookies();
  // Check user permissions (logged in user)
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;

  const [selectedCompanyOption, setSelectedCompanyOption] = React.useState({});
  // Hierarchy picker (Users tab + logged-in cyreen/tenant). Edit Company* is a flat list, not this tree.
  const [hierarchyRetailers, setHierarchyRetailers] = React.useState([]);
  const [newCompanyTree, setNewCompanyTree] = React.useState([]);
  const [selectedHierarchyCompany, setSelectedHierarchyCompany] = React.useState(null);
  const [selectedNewCompany, setSelectedNewCompany] = React.useState(null);
  const [openNewCompanyDropdown, setOpenNewCompanyDropdown] = React.useState(false);
  const [fieldError, setFieldError] = React.useState({});
  // Keep Company / Role unmounted until init finishes so edit does not flash empty then filled fields
  const [isCompanyReady, setIsCompanyReady] = React.useState(false);

  const currentStateData = useSelector((state) => state.userSelections);
  const roleAndPermissions = typeof currentStateData.role_list !== "undefined" ? currentStateData.role_list : [];
  const companyOptionsById =
    typeof currentStateData.company_id_option_obj !== "undefined" ? currentStateData.company_id_option_obj : {};
  const hierarchyCompanyById =
    typeof currentStateData.company_hierarchy_by_id !== "undefined" ? currentStateData.company_hierarchy_by_id : {};

  // Prefer Redux so company visibility stays in sync with the bottom-nav switcher
  const bottomNavRetailerSelection =
    typeof currentStateData.bottom_nav_selected_retailer !== "undefined"
      ? currentStateData.bottom_nav_selected_retailer
      : getBottomNavRetailerSelectionFromCookies();
  const hasBottomNavRetailerSelection = typeof bottomNavRetailerSelection.value !== "undefined";
  const isAllRetailersSelected = bottomNavRetailerSelection.is_all_retailers === true;
  // Hide Company only when a specific retailer was chosen from a real switcher (2+ companies).
  const lockedToSpecificRetailer = isLockedOutsideAllRetailers(
    bottomNavRetailerSelection,
    resolveRetailerSelectionOptions(currentStateData)
  );

  // Users tab + cyreen/tenant admin → hierarchical single-select (not cyreen-user modal)
  const usesHierarchyCompanyPicker =
    cyreenBoolean === false && ["cyreen", "tenant"].includes(loggedInUserType);

  // "hierarchy_single" = Users tab tree; "single" = branch SingleSelect
  const companySelectionMode = usesHierarchyCompanyPicker === true ? "hierarchy_single" : "single";

  const canPickCompanies =
    cyreenBoolean === false && ["cyreen", "tenant", "branch"].includes(loggedInUserType);

  // Hide company when the viewer cannot pick, or a specific retailer is selected (2+ companies)
  const showCompanyField = canPickCompanies === true && lockedToSpecificRetailer !== true;

  const rolesList =
    roleAndPermissions.length !== 0
      ? roleAndPermissions
        .map((roleObj) => {
          return {
            value: roleObj.role_name,
            label: roleObj.role_name,
            deactivated: roleObj.deactivated,
          };
        })
        .filter((roleOption) => {
          if (cyreenBoolean === true) {
            return permissionScope.user_list_can_edit_cyreen === true;
          }
          // Retailer users: Brand / external rules are unchanged
          if (permissionScope.user_list_can_edit_external === true && roleOption.label.toLowerCase() !== "brand") {
            if (usedFor === "update" && userInfo.roles.includes("Brand")) {
              return false;
            }
            return true;
          }
          if (permissionScope.user_list_can_edit_brand === true && roleOption.label.toLowerCase() === "brand") {
            if (usedFor === "update" && !userInfo.roles.includes("Brand")) {
              return false;
            }
            return true;
          }
          return false;
        })
        .filter((role) => cyreenBoolean === true || role.deactivated !== true)
      : [];

  const dispatchAction = useDispatch();

  const handleFieldChange = (user_data) => {
    setUserDetails((previousDetails) => {
      const updatedDetails = {
        ...previousDetails,
        ...user_data,
      };
      dispatchAction(updateStateData(updatedDetails));
      return updatedDetails;
    });
  };

  const mergeUniqueIds = (ids) => {
    return [...new Set(ids.filter((id) => typeof id !== "undefined" && id !== null && id !== ""))];
  };

  // Companies already on the user being edited
  const companiesFromUser = () => {
    if (Array.isArray(userInfo.assigned_companies)) {
      return userInfo.assigned_companies;
    }
    if (typeof userInfo.assigned_company !== "undefined" && userInfo.assigned_company !== null) {
      return [userInfo.assigned_company];
    }
    return [];
  };

  /**
   * Resolve default company id(s) whether the Company field is shown or hidden.
   * Priority: cyreen modal root → update → concrete bottom-nav retailer → current workspace.
   */
  const resolveDefaultCompanyIds = () => {
    // Cyreen-user modal always belongs under the logged-in user's root company
    if (cyreenBoolean === true && typeof userData.root_company_id !== "undefined") {
      return mergeUniqueIds([userData.root_company_id]);
    }

    if (usedFor === "update") {
      // Hierarchy: all assigned companies. Single: workspace_id (same as previous save behaviour).
      if (companySelectionMode === "hierarchy_single") {
        return mergeUniqueIds(companiesFromUser().map((company) => company.id));
      }
      if (typeof userInfo.workspace_id !== "undefined" && userInfo.workspace_id !== "") {
        return mergeUniqueIds([userInfo.workspace_id]);
      }
      return mergeUniqueIds(companiesFromUser().map((company) => company.id));
    }

    // Add + a specific retailer is selected in the bottom nav
    if (hasBottomNavRetailerSelection === true && isAllRetailersSelected === false) {
      const retailerId =
        typeof bottomNavRetailerSelection.id !== "undefined" && bottomNavRetailerSelection.id !== null
          ? bottomNavRetailerSelection.id
          : bottomNavRetailerSelection.value;
      return mergeUniqueIds([retailerId]);
    }

    // Add + all retailers / no bottom-nav selection → current workspace
    if (checkCurrentWorkSpaces.success === true && typeof checkCurrentWorkSpaces.current_workspace?.id !== "undefined") {
      return mergeUniqueIds([checkCurrentWorkSpaces.current_workspace.id]);
    }

    return [];
  };

  const loadRolesForCompanyIds = async (companyIds) => {
    const ids = mergeUniqueIds(companyIds);
    const companyId = ids.length > 0 ? ids[0] : "";

    // Cyreen roles can load without a company. Retailer roles need the selected company.
    if (companyId === "" && roleListType !== "cyreen") {
      return;
    }

    fetchRoles(false, {
      type: roleListType,
      companyId,
    });
  };

  // Users-tab: selected_company_id is the company whose roles are being edited or added.
  const applyHierarchySingleSelection = (option, retainedCompanyIds = [], isAddingNew = false) => {
    const selectedId = option?.id;
    const isRealCompany = typeof selectedId !== "undefined" && selectedId !== null && selectedId !== ADD_TO_NEW_COMPANY.id;

    if (isRealCompany === true && typeof fieldError.user_company !== "undefined") {
      setFieldError((previousErrors) => {
        const { user_company, ...remainingErrors } = previousErrors;
        return remainingErrors;
      });
    }

    handleFieldChange({
      company_selection_mode: "hierarchy_single",
      selected_company_id: isRealCompany === true ? selectedId : "",
      selected_company_ids: isRealCompany === true ? [selectedId] : [],
      workspace_ids: mergeUniqueIds(isRealCompany === true ? [...retainedCompanyIds, selectedId] : retainedCompanyIds),
      user_assigned_company: isRealCompany === true ? { id: selectedId, name: option.label } : {},
      adding_to_new_company: isAddingNew === true,
    });
  };

  // Persist single-company selection into Redux (save reads selected_company_id)
  const applySingleCompanySelection = (companyId, optionForUi = null) => {
    if (typeof companyId === "undefined" || companyId === null || companyId === "") {
      return;
    }

    if (optionForUi !== null) {
      setSelectedCompanyOption(optionForUi);
    }

    const companyDetail = {
      company_selection_mode: "single",
      selected_company_id: companyId,
    };

    if (typeof companyOptionsById[companyId] !== "undefined") {
      companyDetail.user_assigned_company = companyOptionsById[companyId];
    }

    handleFieldChange(companyDetail);
  };

  // Add: empty hierarchy. Update: keep assigned ids for save, and show those
  // companies in New Company* as grey "(added)" rows so the tree stays readable.
  const initializeHierarchySingleUi = async () => {
    const hierarchyInfo = await fetchCompanyHierarchy();
    const nationalTree = nationalCompaniesOnly(hierarchyInfo.success === true ? hierarchyInfo.retailers : []);
    const assignedCompanyIds = mergeUniqueIds(companiesFromUser().map((company) => company.id));

    setHierarchyRetailers(nationalTree);
    setNewCompanyTree(markAlreadyAddedCompanies(nationalTree, assignedCompanyIds));
    setSelectedHierarchyCompany(null);
    setSelectedNewCompany(null);
    setOpenNewCompanyDropdown(false);

    handleFieldChange({
      company_selection_mode: "hierarchy_single",
      selected_company_id: "",
      selected_company_ids: [],
      workspace_ids: usedFor === "update" ? assignedCompanyIds : [],
      adding_to_new_company: false,
    });
  };

  // One init path for company context (shown or hidden)
  const initializeCompanyContext = async () => {
    const defaultCompanyIds = resolveDefaultCompanyIds();

    if (companySelectionMode === "hierarchy_single") {
      await initializeHierarchySingleUi();

      if (showCompanyField !== true) {
        const fallbackOption = defaultCompanyIds.length > 0
          ? toCompanyOption(
              typeof hierarchyCompanyById[defaultCompanyIds[0]] !== "undefined"
                ? hierarchyCompanyById[defaultCompanyIds[0]]
                : { id: defaultCompanyIds[0], name: String(defaultCompanyIds[0]) }
            )
          : null;
        applyHierarchySingleSelection(fallbackOption, defaultCompanyIds);
        await loadRolesForCompanyIds(defaultCompanyIds);
      }
      return;
    }

    // Single-company mode (dropdown may or may not be visible)
    if (showCompanyField === true) {
      fetchCompanies("users");
    }

    if (defaultCompanyIds.length === 0) {
      return;
    }

    const companyId = defaultCompanyIds[0];
    const optionForUi =
      usedFor === "update" && typeof userInfo.assigned_company !== "undefined"
        ? { id: userInfo.assigned_company.id, label: userInfo.assigned_company.name }
        : checkCurrentWorkSpaces.success === true && checkCurrentWorkSpaces.current_workspace?.id === companyId
          ? { id: checkCurrentWorkSpaces.current_workspace.id, label: checkCurrentWorkSpaces.current_workspace.name }
          : hasBottomNavRetailerSelection === true &&
              (bottomNavRetailerSelection.id === companyId || bottomNavRetailerSelection.value === companyId)
            ? {
              id: companyId,
              label:
                typeof bottomNavRetailerSelection.label !== "undefined"
                  ? bottomNavRetailerSelection.label
                  : String(companyId),
            }
            : { id: companyId, label: String(companyId) };

    applySingleCompanySelection(companyId, optionForUi);
    await loadRolesForCompanyIds([companyId]);
  };

  React.useEffect(() => {
    const initializeUserModal = async () => {
      try {
        handleFieldChange({ ...userInfo, cyreen_user: cyreenBoolean });
        await initializeCompanyContext();

        // Update starts with the user's current roles; picking a company loads that company's roles
        if (usedFor === "update" && Array.isArray(userInfo.roles)) {
          setSelectedRolePermissions(
            userInfo.roles.map((roleName) => {
              return { value: roleName, label: roleName };
            })
          );
        }
      } finally {
        setIsCompanyReady(true);
      }
    };

    initializeUserModal();
  }, []);

  const isAddingToNewCompany = selectedHierarchyCompany?.id === ADD_TO_NEW_COMPANY.id;
  const selectedRoleCompanyId = isAddingToNewCompany === true
    ? selectedNewCompany?.id
    : selectedHierarchyCompany?.id;
  // Role stays grey until a real company is picked (assigned company or New Company*)
  const rolesAreLocked =
    companySelectionMode === "hierarchy_single" &&
    showCompanyField === true &&
    (typeof selectedRoleCompanyId === "undefined" ||
      selectedRoleCompanyId === null ||
      selectedRoleCompanyId === ADD_TO_NEW_COMPANY.id);

  const assignedCompanyIdsForSave = mergeUniqueIds(companiesFromUser().map((company) => company.id));

  // Edit Company*: assigned companies in the list; "Add to new company" is the pinned leading option
  const assignedCompanyFlatOptions = companiesFromUser()
    .map((company) => toCompanyOption(company))
    .sort((first, second) => String(first.label).localeCompare(String(second.label)));

  const restoreRolesFromUserInfo = () => {
    const roles = Array.isArray(userInfo.roles) ? userInfo.roles : [];
    setSelectedRolePermissions(
      roles.map((roleName) => {
        return { value: roleName, label: roleName };
      })
    );
    handleFieldChange({ roles });
  };

  // Load the roles already assigned on that company so they can be changed
  const fillRolesFromAssignedCompany = (companyId) => {
    const assignedCompany = companiesFromUser().find((company) => String(company.id) === String(companyId));
    const companyRoles = Array.isArray(assignedCompany?.roles) ? assignedCompany.roles : [];
    const roleOptions = companyRoles.map((roleName) => {
      return { value: roleName, label: roleName };
    });
    setSelectedRolePermissions(roleOptions);
    handleFieldChange({ roles: companyRoles });
  };

  const handleUsersTabCompanyChange = (option) => {
    if (option === null) {
      setSelectedHierarchyCompany(null);
      setSelectedNewCompany(null);
      setOpenNewCompanyDropdown(false);
      applyHierarchySingleSelection(null, usedFor === "update" ? assignedCompanyIdsForSave : []);
      if (usedFor === "update") {
        restoreRolesFromUserInfo();
      } else {
        setSelectedRolePermissions([]);
        handleFieldChange({ roles: [] });
      }
      return;
    }

    if (option.id === ADD_TO_NEW_COMPANY.id) {
      setSelectedHierarchyCompany(ADD_TO_NEW_COMPANY);
      setSelectedNewCompany(null);
      setOpenNewCompanyDropdown(true);
      setSelectedRolePermissions([]);
      handleFieldChange({ roles: [] });
      applyHierarchySingleSelection(null, assignedCompanyIdsForSave, true);
      return;
    }

    setSelectedHierarchyCompany(option);
    setSelectedNewCompany(null);
    setOpenNewCompanyDropdown(false);
    applyHierarchySingleSelection(option, usedFor === "update" ? assignedCompanyIdsForSave : []);
    if (usedFor === "update") {
      fillRolesFromAssignedCompany(option.id);
    } else {
      setSelectedRolePermissions([]);
      handleFieldChange({ roles: [] });
    }
    loadRolesForCompanyIds([option.id]);
  };

  const handleNewCompanyChange = (option) => {
    setSelectedNewCompany(option);
    setOpenNewCompanyDropdown(false);
    // Stay in "add new" until Company* is cleared — validation still requires this pick
    if (option === null) {
      applyHierarchySingleSelection(null, assignedCompanyIdsForSave, true);
      setSelectedRolePermissions([]);
      handleFieldChange({ roles: [] });
      return;
    }
    applyHierarchySingleSelection(option, assignedCompanyIdsForSave, true);
    setSelectedRolePermissions([]);
    handleFieldChange({ roles: [] });
    loadRolesForCompanyIds([option.id]);
  };

  React.useEffect(() => {
    if (rolesAreLocked === true || usedFor === "update" || selectedRolePermissions.length !== 0 || rolesList.length !== 1) {
      return;
    }

    const autoSelectedRole = rolesList[0];
    setSelectedRolePermissions([autoSelectedRole]);
    handleFieldChange({
      roles: [autoSelectedRole.label],
    });
  }, [roleAndPermissions]);

  return (
    <Dialog
      open={open}
      onClose={(e) => {
        e.preventDefault();
      }}
      fullWidth={true}
      maxWidth={size}
    >
      <DialogTitle> {usedFor === "update" ? updateuserText : adduserText}</DialogTitle>
      <DialogContent>
        <Box>
          {/* ------- username / email --------- */}
          <Stack direction={"row"} spacing={4}>
            <TextInputField
              fieldRequired={true}
              readOnlyFlag={usedFor === "update" ? true : false}
              elemId="userName"
              label="User Name"
              fieldName="user_name"
              showlabel={true}
              defaultVal={typeof userDetails.username !== "undefined" ? userDetails.username : ""}
              inputType={"text"}
              onChangeCall={(e) => {
                e.preventDefault();
                const value = e.target.value;
                if (value.includes(" ")) {
                  setFieldError({ ...fieldError, user_name: "Username must not contain spaces" });
                } else if (fieldError.user_name) {
                  const { user_name, ...rest } = fieldError;
                  setFieldError(rest);
                }
                handleFieldChange({
                  username: value.replace(/\s/g, ""),
                });
              }}
              fieldError={
                typeof fieldError.user_name !== "undefined" && fieldError.user_name !== "" ? fieldError.user_name : ""
              }
            />
            <TextInputField
              fieldRequired={true}
              elemId="email"
              readOnlyFlag={usedFor === "update" ? true : false}
              label="E-Mail"
              fieldName="email"
              showlabel={true}
              defaultVal={typeof userDetails.email !== "undefined" ? userDetails.email : ""}
              inputType={"text"}
              onChangeCall={(e) => {
                e.preventDefault();
                const value = e.target.value;
                if (value.includes(" ")) {
                  setFieldError({ ...fieldError, email: "E-Mail must not contain spaces" });
                } else if (fieldError.email) {
                  const { email, ...rest } = fieldError;
                  setFieldError(rest);
                }
                handleFieldChange({
                  email: value.replace(/\s/g, ""),
                });
              }}
              fieldError={typeof fieldError.email !== "undefined" && fieldError.email !== "" ? fieldError.email : ""}
            />
          </Stack>

          {isCompanyReady === true && (
          <>
          {/* Users tab: Add uses the hierarchy. Edit uses a flat list (assigned + Add) + optional New Company*. */}
          {showCompanyField === true && companySelectionMode === "hierarchy_single" && (
            <>
              <Box height={40} />
              <Stack direction={"row"} spacing={4}>
                {usedFor === "update" ? (
                  <SingleSelect
                    label="Company*"
                    enableFullWidth={true}
                    disableSearch={true}
                    sortOptions={false}
                    allowClearance={true}
                    selectionCheck={(option, value) => option.id === value.id}
                    leadingOption={ADD_TO_NEW_COMPANY}
                    options={assignedCompanyFlatOptions}
                    defaultVal={
                      isAddingToNewCompany === true
                        ? ADD_TO_NEW_COMPANY
                        : selectedHierarchyCompany
                    }
                    onChangeCall={handleUsersTabCompanyChange}
                    fieldError={
                      typeof fieldError.user_company !== "undefined" && fieldError.user_company !== ""
                        ? fieldError.user_company
                        : ""
                    }
                  />
                ) : (
                  <CompanyHierarchySingleSelect
                    label="Company*"
                    enableFullWidth={true}
                    showSearch={true}
                    retailers={hierarchyRetailers}
                    value={selectedHierarchyCompany}
                    onChange={handleUsersTabCompanyChange}
                    fieldError={
                      typeof fieldError.user_company !== "undefined" && fieldError.user_company !== ""
                        ? fieldError.user_company
                        : ""
                    }
                  />
                )}
                {isAddingToNewCompany === true && (
                  <CompanyHierarchySingleSelect
                    label="New Company*"
                    enableFullWidth={true}
                    showSearch={true}
                    autoOpen={openNewCompanyDropdown}
                    retailers={newCompanyTree}
                    value={selectedNewCompany}
                    onChange={handleNewCompanyChange}
                    disabledOptions={isAlreadyAddedCompany}
                    tooltipTitleForDisabledOptions={ALREADY_ADDED_COMPANY_TOOLTIP}
                    fieldError={
                      typeof fieldError.user_company !== "undefined" && fieldError.user_company !== ""
                        ? fieldError.user_company
                        : ""
                    }
                  />
                )}
              </Stack>
            </>
          )}

          {/* ------------ company (single) -------------- */}
          {showCompanyField === true && companySelectionMode === "single" && (
            <>
              <Box height={40} />
              <Stack direction={"row"} spacing={4}>
                <SingleSelect
                  enableFullWidth={true}
                  label={"Company*"}
                  readOnlyFlag={false}
                  selectionCheck={(option, value) => option.label === value.label}
                  sortOptions={false}
                  options={
                    typeof currentStateData.company_name_list !== "undefined" ? currentStateData.company_name_list : []
                  }
                  defaultVal={selectedCompanyOption}
                  onChangeCall={(selectedOption) => {
                    // On update, keep selected_company_id unchanged (existing behaviour)
                    if (usedFor === "update") {
                      setSelectedCompanyOption(selectedOption);
                      handleFieldChange({
                        user_assigned_company: companyOptionsById[selectedOption.id],
                      });
                    } else {
                      applySingleCompanySelection(selectedOption.id, selectedOption);
                    }
                    loadRolesForCompanyIds([selectedOption.id]);
                  }}
                  fieldError={
                    typeof fieldError.user_company !== "undefined" && fieldError.user_company !== ""
                      ? fieldError.user_company
                      : ""
                  }
                />
              </Stack>
            </>
          )}

          <Box height={40} />
          {/* ----------- roles ------------ */}
          <Stack direction={"row"} spacing={4}>
            {rolesAreLocked === true ? (
              <HtmlTooltipDisabled title="Select Company">
                <span style={{ width: "100%", display: "block" }}>
                  <GroupedMultiSelect
                    enableFullWidth={true}
                    elemId={"selectRoleDropdown"}
                    label={"User role*"}
                    showSelectAll={false}
                    readOnlyFlag={true}
                    disabledIfSelectedOption={["Brand"]}
                    showGroupNames={false}
                    options={[]}
                    selectionCheck={(option, value) => option.label === value.label}
                    defaultVal={[]}
                    onChangeCall={() => {}}
                    fieldError=""
                  />
                </span>
              </HtmlTooltipDisabled>
            ) : (
            <GroupedMultiSelect
              enableFullWidth={true}
              elemId={"selectRoleDropdown"}
              label={"User role*"}
              showSelectAll={false}
              disabledIfSelectedOption={["Brand"]}
              showGroupNames={false}
              options={
                rolesList.length !== 0
                  ? [...rolesList].sort((a, b) => {
                      return a.label.localeCompare(b.label);
                    })
                  : []
              }
              selectionCheck={(option, value) => option.label === value.label}
              defaultVal={selectedRolePermissions}
              onChangeCall={(selectedOption) => {
                const roleLabels = selectedOption.map((roleDtl) => roleDtl.label);
                setSelectedRolePermissions(selectedOption);
                handleFieldChange({
                  roles: roleLabels,
                });
              }}
              fieldError={
                typeof fieldError.user_role !== "undefined" && fieldError.user_role !== "" ? fieldError.user_role : ""
              }
            />
            )}
          </Stack>

          {/* ----------- brand campaign selection ---------- */}
          {typeof userDetails.roles !== "undefined" && userDetails.roles.includes("Brand") && (
            <>
              <Box height={40} />
              <Stack direction={"row"} spacing={4}>
                <MultiSelect
                  label="Select Campaign*"
                  enableFullWidth={true}
                  options={
                    typeof currentStateData.campaign_names_info !== "undefined" &&
                      typeof currentStateData.campaign_names_info.report_available_campaigns !== "undefined"
                      ? currentStateData.campaign_names_info.report_available_campaigns
                      : []
                  }
                  defaultVal={
                    typeof userDetails.brand_campaign_selection !== "undefined"
                      ? userDetails.brand_campaign_selection
                      : []
                  }
                  selectionCheck={(option, value) => option.id === value.id}
                  onChangeCall={(selectedOption) => {
                    let selectedCampaigns = [];
                    selectedOption.forEach((option) => {
                      selectedCampaigns.push(option.id);
                    });

                    handleFieldChange({
                      campaign_id: selectedCampaigns,
                      brand_campaign_selection: selectedOption,
                    });
                  }}
                />
              </Stack>
            </>
          )}
          <Box height={40} />
          </>
          )}

          {cyreenBoolean === true && (
            <StyledInfoBox>
              <Typography variant="body2">
                Important: Never add a customer as Cyreen user. This user type is reserved for employees of Cyreen.
              </Typography>
            </StyledInfoBox>
          )}
        </Box>
      </DialogContent>
      <Box height={20} />
      <DialogActions>
        <Stack direction={"row"} spacing={2}>
          <CloseButton onClick={onClose} label="Cancel" icon={<Close />} />
          <ActionButton
            disabled={isCompanyReady !== true}
            onClick={(e) => {
              e.preventDefault();
              setFieldError({});

              const userId = { id: typeof userDetails.id !== "undefined" ? userDetails.id : "" };
              saveUserInformation(usedFor, userId, refreshUserType).then((info) => {
                if (info.success === true) {
                  const message = usedFor === "update" ? "User updated successfully" : "User added successfully";
                  toast(message, { type: "success" });
                  closeModal();
                } else {
                  setFieldError(info.errorMessages);
                }
              });
            }}
            label={usedFor === "update" ? "Update" : "Save"}
            icon={<Save />}
          />
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default AddUser;
